import React, { useState, useEffect } from 'react';
import {
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Button, Dialog, DialogTitle, DialogContent,
  MenuItem, TablePagination
} from '@mui/material';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';

const CrewAnalytics = () => {
  const [crews, setCrews] = useState([]);
  const [filteredCrews, setFilteredCrews] = useState([]);
  const [searchCrew, setSearchCrew] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [searchDesignation, setSearchDesignation] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isProjectsDialogOpen, setIsProjectsDialogOpen] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch crews with descending order by created_at
      const { data: crewsData, error: crewsError } = await supabase
        .from('crew')
        .select('crew_id, Name, Phone, Designation, created_at')
        .order('created_at', { ascending: false });

      if (crewsError) throw crewsError;

      // Fetch crew-project mappings with project details
      const { data: mappingsData, error: mappingsError } = await supabase
        .from('crew_project_mapping')
        .select(`
          crew_id,
          project_id,
          project:project(project_id, project_name, created_at)
        `);

      if (mappingsError) throw mappingsError;

      // Group crews by Name and Phone
      const groupedCrews = crewsData.reduce((acc, crew) => {
        const key = `${crew.Name}-${crew.Phone}`;
        if (!acc[key]) {
          acc[key] = {
            ...crew,
            created_at: crew.created_at,
            crew_ids: [crew.crew_id]
          };
        } else {
          acc[key].crew_ids.push(crew.crew_id);
          // Keep the earliest creation date
          if (new Date(crew.created_at) < new Date(acc[key].created_at)) {
            acc[key].created_at = crew.created_at;
          }
        }
        return acc;
      }, {});

      // Process data for each unique crew
      const combinedCrews = Object.values(groupedCrews).map(crew => {
        // Get all projects for this crew's IDs
        const relatedProjects = mappingsData
          .filter(mapping => crew.crew_ids.includes(mapping.crew_id))
          .map(mapping => mapping.project)
          .filter(Boolean);

        // Remove duplicate projects
        const uniqueProjects = [...new Map(relatedProjects.map(p => [p.project_id, p])).values()]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        return {
          ...crew,
          projects: uniqueProjects
        };
      });

      // Extract unique designations (case-insensitive)
      const uniqueDesignations = Array.from(
        new Map(
          crewsData
            .map(crew => crew.Designation)
            .filter(Boolean)
            .map(designation => designation.trim()) // Remove whitespace
            .map(designation => [designation.toLowerCase(), designation]) // Use lowercase as key for uniqueness
        ).values()
      ).sort((a, b) => a.localeCompare(b)); // Sort alphabetically

      setDesignations(uniqueDesignations);
      setCrews(combinedCrews);
      setFilteredCrews(combinedCrews);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSearch = () => {
    let filtered = crews;

    if (searchCrew) {
      filtered = filtered.filter(crew =>
        crew.Name?.toLowerCase().includes(searchCrew.toLowerCase())
      );
    }

    if (searchMobile) {
      filtered = filtered.filter(crew =>
        crew.Phone?.includes(searchMobile)
      );
    }

    if (searchDesignation) {
      filtered = filtered.filter(crew =>
        crew.Designation?.toLowerCase() === searchDesignation.toLowerCase()
      );
    }

    // Sort by created_at in descending order
    filtered = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredCrews(filtered);
    setPage(0);
  };

  const handleProjectsClick = (projects) => {
    setSelectedProjects(projects);
    setIsProjectsDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Get current page data
  const paginatedCrews = filteredCrews.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="crew-analytics">
      <h2>Crew Analytics</h2>

      {/* Search Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Crew Name"
          value={searchCrew}
          onChange={(e) => setSearchCrew(e.target.value)}
        />
        <TextField
          label="Search by Mobile"
          value={searchMobile}
          onChange={(e) => setSearchMobile(e.target.value)}
        />
        <TextField
          select
          label="Designation"
          value={searchDesignation}
          onChange={(e) => setSearchDesignation(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Designations</MenuItem>
          {designations.map((designation) => (
            <MenuItem key={designation} value={designation}>
              {designation}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Crews Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Crew Name</TableCell>
              <TableCell>Mobile Number</TableCell>
              <TableCell>Designation</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>No. of Projects</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCrews.map((crew, index) => (
              <TableRow key={`${crew.Name}-${crew.Phone}`}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{crew.Name}</TableCell>
                <TableCell>{crew.Phone}</TableCell>
                <TableCell>{crew.Designation}</TableCell>
                <TableCell>
                  {format(new Date(crew.created_at), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleProjectsClick(crew.projects)}
                    variant="text"
                    color="primary"
                    disabled={crew.projects.length === 0}
                  >
                    {crew.projects.length}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredCrews.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20]}
        />
      </TableContainer>

      {/* Projects Dialog */}
      <Dialog
        open={isProjectsDialogOpen}
        onClose={() => setIsProjectsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Projects</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S. No.</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Creation Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedProjects.map((project, index) => (
                  <TableRow key={project.project_id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{project.project_name}</TableCell>
                    <TableCell>
                      {format(new Date(project.created_at), 'dd/MM/yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrewAnalytics; 